import { Check } from "lucide-react";
import { TID } from "@/lib/testIds";

const MAP_IMG =
  "https://images.unsplash.com/photo-1650526087824-163941841b52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxsb2dpc3RpY3MlMjBnbG9iYWwlMjBuZXR3b3JrJTIwbWFwfGVufDB8fHx8MTc4Mjc0MjEzOXww&ixlib=rb-4.1.0&q=85";

const presence = [
  { key: "india", flag: "🇮🇳", name: "India", status: "Operational" },
  { key: "united-kingdom", flag: "🇬🇧", name: "United Kingdom", status: "Operational" },
  { key: "middle-east", flag: "🇸🇦", name: "Middle East", status: "Expansion" },
];

const lanes = [
  "Asia",
  "Europe",
  "GCC Countries",
  "North America",
  "Africa",
  "Southeast Asia",
  "United Kingdom",
  "Middle East",
];

export default function Coverage() {
  return (
    <section
      id="coverage"
      data-testid={TID.coverage.section}
      className="relative bg-blue-950 text-white overflow-hidden grain"
    >
      <img src={MAP_IMG} alt="JMR Global Logistics global freight network — international trade lanes" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-blue-950 via-blue-950/85 to-blue-950" />
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-7 reveal">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-sky-400 mb-4">
              Our Global Presence
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              Serving cargo<br />
              <span className="text-sky-400">across continents.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end reveal">
            <p className="text-slate-300 text-lg md:text-xl max-w-md">
              Serving more than <span className="text-sky-300 font-bold">100+ trade lanes</span> worldwide through our international logistics network.
            </p>
          </div>
        </div>

        {/* Presence pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-white/15 mb-16">
          {presence.map((p) => (
            <div
              key={p.key}
              data-testid={TID.coverage.region(p.key)}
              className="border-r border-b border-white/15 p-8 md:p-10 hover:bg-white/[0.04] transition-colors group reveal"
            >
              <div className="text-5xl md:text-6xl mb-4">{p.flag}</div>
              <p className="font-display font-black text-2xl md:text-3xl group-hover:text-sky-400 transition-colors mb-2">
                {p.name}
              </p>
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 ${p.status === "Operational" ? "bg-green-400" : "bg-sky-400"} animate-pulse`} />
                <span className="font-mono text-xs uppercase tracking-widest text-white/60">{p.status}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Trade lanes */}
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-400 mb-6 reveal">Active Trade Lanes</p>
        <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-white/15">
          {lanes.map((r) => (
            <div
              key={r}
              data-testid={TID.coverage.region(r.toLowerCase().replace(/[^a-z0-9]+/g, "-") + "-lane")}
              className="border-r border-b border-white/15 p-5 md:p-6 hover:bg-white/[0.04] transition-colors group reveal"
            >
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 bg-sky-400 text-blue-950 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
                <p className="font-display font-semibold text-base md:text-lg group-hover:text-sky-400 transition-colors">
                  {r}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Marquee */}
        <div className="mt-16 md:mt-24 overflow-hidden border-y border-white/15 py-6 no-scrollbar">
          <div className="flex marquee-track whitespace-nowrap gap-12">
            {[...Array(2)].map((_, dup) => (
              <div key={dup} className="flex items-center gap-12 shrink-0">
                {[
                  "OCEAN FREIGHT",
                  "AIR FREIGHT",
                  "ROAD TRANSPORT",
                  "WAREHOUSING",
                  "CUSTOMS BROKERAGE",
                  "PROJECT LOGISTICS",
                  "DOOR-TO-DOOR",
                  "100+ TRADE LANES",
                ].map((w) => (
                  <span key={w + dup} className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white/30 flex items-center gap-12">
                    {w}
                    <span className="text-sky-400">★</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
